import type { ButtonHTMLAttributes, KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { Icon, CheckMark } from './Icon';
import type { IconName } from './icons';
import type { StageColor } from '@shared/types';

// Small building blocks from the design: keys, chips, dots, switches, checkboxes, spinners, empty states.

/** Keyboard keys from a space-separated list: "G T" → two keys. */
export function Kbds({ keys }: { keys: string }) {
  return (
    <>
      {keys.split(' ').map((k, i) => (
        <kbd key={i} className="kbd">
          {k}
        </kbd>
      ))}
    </>
  );
}

/** "G then T". */
export function KeysThen({ first, then }: { first: string; then: string }) {
  return (
    <>
      <Kbds keys={first} />
      <span>then</span>
      <Kbds keys={then} />
    </>
  );
}

export type ChipTone = 'accent' | 'success' | 'warning' | 'danger' | 'info' | 'navy' | 'plain';

export function Chip({
  tone,
  icon,
  large,
  className,
  children,
  title,
}: {
  tone?: ChipTone;
  icon?: IconName;
  large?: boolean;
  className?: string;
  children: ReactNode;
  title?: string;
}) {
  const cls = ['chip', tone ? `chip-${tone}` : '', large ? 'chip-lg' : '', className].filter(Boolean).join(' ');
  return (
    <span className={cls} title={title}>
      {icon ? <Icon name={icon} /> : null}
      {children}
    </span>
  );
}

export function Dot({ color, className }: { color?: StageColor | 'danger' | 'warning' | 'success' | 'accent'; className?: string }) {
  return <span className={['dot', color, className].filter(Boolean).join(' ')} aria-hidden="true" />;
}

/** An on/off switch. Give it a visible label as children, or an accessible one through `label`. */
export function Switch({
  checked,
  onChange,
  label,
  disabled,
  id,
  className,
  children,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
  /** Text shown next to the switch; the whole row toggles it. */
  children?: ReactNode;
}) {
  if (children) {
    return (
      <button
        type="button"
        id={id}
        className={['switch-l', className].filter(Boolean).join(' ')}
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange(!checked)}
      >
        <span className="switch" aria-hidden="true" />
        {children}
      </button>
    );
  }
  return (
    <button
      type="button"
      id={id}
      className={['switch', className].filter(Boolean).join(' ')}
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
    />
  );
}

/** Square checkbox used for selecting rows. `checked` may be "mixed" for a partly selected list. */
export function Checkbox({
  checked,
  onChange,
  label,
  className,
  ...rest
}: {
  checked: boolean | 'mixed';
  onChange: (next: boolean, e: MouseEvent) => void;
  label: string;
  className?: string;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange'>) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      className={className ? `cbx ${className}` : 'cbx'}
      onClick={(e) => {
        e.stopPropagation();
        onChange(checked !== true, e);
      }}
      {...rest}
    >
      <CheckMark />
    </button>
  );
}

/** Round checkbox used for completing follow-ups. */
export function RoundCheck({ checked, onChange, label }: { checked: boolean; onChange: (next: boolean) => void; label: string }) {
  return (
    <button
      type="button"
      className="rcheck"
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      onClick={(e) => {
        e.stopPropagation();
        onChange(!checked);
      }}
    >
      <CheckMark />
    </button>
  );
}

export function Spinner({ label = 'Loading', className }: { label?: string; className?: string }) {
  return <span className={className ? `spin ${className}` : 'spin'} role="status" aria-label={label} />;
}

/** A centred loading state for a panel or view. */
export function Loading({ label = 'Loading', className }: { label?: string; className?: string }) {
  return (
    <div className={['loading-state', className].filter(Boolean).join(' ')} role="status" aria-live="polite">
      <span className="spin" aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}

/** Placeholder bars while a list loads. */
export function Skeleton({ rows = 4, className }: { rows?: number; className?: string }) {
  return (
    <div className={['skel-rows', className].filter(Boolean).join(' ')} aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skel-row">
          <span className="skel" style={{ width: 28, height: 28, borderRadius: 9 }} />
          <span className="skel" style={{ flex: 1, maxWidth: `${70 - ((i * 13) % 30)}%` }} />
          <span className="skel" style={{ width: 54 }} />
        </div>
      ))}
    </div>
  );
}

export function Empty({
  icon = 'check-circle',
  title,
  children,
  action,
  tone = 'neutral',
  compact,
  className,
}: {
  icon?: IconName;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
  /** Colour of the round art: neutral for "nothing here", green only for "all done". */
  tone?: 'neutral' | 'success';
  /** Smaller art and less room, for small tiles, popovers and lists inside dialogs. */
  compact?: boolean;
  className?: string;
}) {
  return (
    <div className={['empty', compact ? 'compact' : '', className].filter(Boolean).join(' ')}>
      <div className={tone === 'success' ? 'empty-art success' : 'empty-art'}>
        <Icon name={icon} />
      </div>
      <h3>{title}</h3>
      {children ? <p>{children}</p> : null}
      {action ? <div className="empty-act">{action}</div> : null}
    </div>
  );
}

export type CalloutTone = 'warn' | 'danger' | 'info' | 'success' | 'plain';

const CALLOUT_ICON: Record<CalloutTone, IconName> = {
  warn: 'warn',
  danger: 'x-circle',
  info: 'info',
  success: 'check-circle',
  plain: 'info',
};

/** A boxed message. The first bold line becomes its heading. */
export function Callout({
  tone = 'plain',
  icon,
  title,
  children,
  actions,
  className,
  role,
}: {
  tone?: CalloutTone;
  icon?: IconName;
  title?: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  className?: string;
  role?: 'alert' | 'status';
}) {
  return (
    <div className={['callout', tone === 'plain' ? '' : tone, className].filter(Boolean).join(' ')} role={role}>
      <Icon name={icon ?? CALLOUT_ICON[tone]} />
      <div className="grow">
        {title ? <b>{title}</b> : null}
        {children}
        {actions ? <div className="acts">{actions}</div> : null}
      </div>
    </div>
  );
}

export function Progress({ value, navy, animated, label }: { value: number; navy?: boolean; animated?: boolean; label?: string }) {
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div
      className={['prog', navy ? 'navy' : '', animated ? 'anim' : ''].filter(Boolean).join(' ')}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pct)}
      aria-label={label}
    >
      <i style={{ width: `${pct}%` }} />
    </div>
  );
}

/** Pill tabs inside sheets and panels. */
export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
  label,
  className,
}: {
  tabs: { id: T; label: string; icon?: IconName; count?: number | string }[];
  value: T;
  onChange: (id: T) => void;
  label: string;
  className?: string;
}) {
  const move = (e: KeyboardEvent, i: number) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const next = tabs[(i + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length]!;
    onChange(next.id);
    const list = (e.currentTarget as HTMLElement).parentElement;
    requestAnimationFrame(() => list?.querySelector<HTMLElement>(`[data-tab="${next.id}"]`)?.focus());
  };
  return (
    <div className={['tabs', className].filter(Boolean).join(' ')} role="tablist" aria-label={label}>
      {tabs.map((t, i) => (
        <button
          key={t.id}
          type="button"
          className="tab"
          role="tab"
          data-tab={t.id}
          aria-selected={t.id === value}
          tabIndex={t.id === value ? 0 : -1}
          onClick={() => onChange(t.id)}
          onKeyDown={(e) => move(e, i)}
        >
          {t.icon ? <Icon name={t.icon} className="i-sm" /> : null}
          {t.label}
          {t.count !== undefined ? <span className="cnt">{t.count}</span> : null}
        </button>
      ))}
    </div>
  );
}

/** Segmented control (two to four options). */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
  className,
}: {
  options: { id: T; label: ReactNode; icon?: IconName }[];
  value: T;
  onChange: (id: T) => void;
  label: string;
  className?: string;
}) {
  return (
    <div className={['segc', className].filter(Boolean).join(' ')} role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.id} type="button" aria-pressed={o.id === value} onClick={() => onChange(o.id)}>
          {o.icon ? <Icon name={o.icon} className="i-sm" /> : null}
          {o.label}
        </button>
      ))}
    </div>
  );
}
